import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const scrollPositions = new Map();      // location.key -> window scrollY
const containerPositions = new Map();   // `${location.key}::${scrollKey}` -> { left, top }
const MAX_RESTORE_MS = 5000;

function ScrollRestoration() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const restoreRafRef = useRef(null);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // Continuously save scroll positions against the current location.key — both
  // the window and any inner overflow container tagged with data-scroll-key.
  // Don't save in cleanup: by then React has swapped the DOM, scroll offsets may
  // have been clamped to the new (shorter) page, and saving that overwrites the
  // value the listener already captured.
  useEffect(() => {
    const onScroll = (event) => {
      const target = event.target;
      // The document's own scroll fires with target === document.
      if (target === document || target === document.documentElement || target === document.body) {
        scrollPositions.set(location.key, window.scrollY);
        return;
      }
      // Inner container scroll. These events fire on the element and don't
      // bubble, so the listener below is registered in the capture phase.
      if (target && target.nodeType === 1 && target.dataset && target.dataset.scrollKey) {
        containerPositions.set(`${location.key}::${target.dataset.scrollKey}`, {
          left: target.scrollLeft,
          top: target.scrollTop,
        });
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => window.removeEventListener("scroll", onScroll, { capture: true });
  }, [location.key]);

  // Restore on React Router navigation (POP) — handles back/forward when React
  // actually re-renders (i.e. the page wasn't served from bfcache).
  useEffect(() => {
    if (restoreRafRef.current) {
      cancelAnimationFrame(restoreRafRef.current);
      restoreRafRef.current = null;
    }
    if (navigationType !== "POP") {
      window.scrollTo(0, 0);
      return undefined;
    }

    const savedWindow = scrollPositions.get(location.key) ?? 0;
    const startTime = performance.now();

    // Both the window and inner containers may need to wait for async content
    // (the car lists are fetched after mount) to grow back to their saved
    // height before the offset is reachable — so retry every frame until every
    // target is settled or the time budget runs out.
    const tryScroll = () => {
      const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      window.scrollTo({ top: Math.min(savedWindow, maxY), left: 0, behavior: "instant" });
      let settled = window.scrollY >= savedWindow - 1;

      document.querySelectorAll("[data-scroll-key]").forEach((el) => {
        const saved = containerPositions.get(`${location.key}::${el.dataset.scrollKey}`);
        if (!saved) return;
        const maxLeft = Math.max(0, el.scrollWidth - el.clientWidth);
        const maxTop = Math.max(0, el.scrollHeight - el.clientHeight);
        // Use scrollTo with behavior:"instant" rather than assigning
        // scrollLeft/scrollTop: these containers set `scroll-behavior: smooth`
        // in CSS, which makes a plain assignment animate. Re-assigning every
        // frame would restart that animation and pin the container near 0 for
        // the whole retry window. "instant" overrides the CSS and jumps.
        el.scrollTo({
          top: Math.min(saved.top, maxTop),
          left: Math.min(saved.left, maxLeft),
          behavior: "instant",
        });
        if (el.scrollTop < saved.top - 1 || el.scrollLeft < saved.left - 1) {
          settled = false;
        }
      });

      if (!settled && performance.now() - startTime < MAX_RESTORE_MS) {
        restoreRafRef.current = requestAnimationFrame(tryScroll);
      } else {
        restoreRafRef.current = null;
      }
    };
    restoreRafRef.current = requestAnimationFrame(tryScroll);
    return () => {
      if (restoreRafRef.current) {
        cancelAnimationFrame(restoreRafRef.current);
        restoreRafRef.current = null;
      }
    };
  }, [location.key, navigationType]);

  // Restore on bfcache restoration — when the browser shows a previous page
  // from its back/forward cache, React doesn't re-render, so the POP effect
  // above never fires. `pageshow` with persisted=true is the signal. bfcache
  // already preserves inner container scroll, so only the window needs help.
  useEffect(() => {
    const onPageShow = (event) => {
      if (!event.persisted) return;
      const saved = scrollPositions.get(location.key);
      if (saved !== undefined) {
        window.scrollTo({ top: saved, left: 0, behavior: "instant" });
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [location.key]);

  return null;
}

export default ScrollRestoration;
